import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { DEFAULT_PREFERENCES, CONSENT_KEY_PREFIX, readConsent, saveConsent } from '../utils/cookieConsent';
import CookieBanner from '../Components/CookieBanner';

const CookieConsentContext = createContext({
  preferences: DEFAULT_PREFERENCES,
  openPreferences: () => {},
  savePreferences: () => {},
});

function ScopedCookieConsentProvider({ scope, children }) {
  const [record, setRecord] = useState(() => readConsent(scope));
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const preferences = record?.preferences || DEFAULT_PREFERENCES;
  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const savePreferences = useCallback(next => {
    setRecord(saveConsent(scope, next));
    setPreferencesOpen(false);
  }, [scope]);

  useEffect(() => {
    const sync = event => {
      if (event?.type === 'storage' && event.key !== CONSENT_KEY_PREFIX + scope && event.key !== null) return;
      // A blocked-storage choice remains valid in memory for this page.
      if (record?.persisted === false) return;
      setRecord(readConsent(scope));
    };
    const expiry = setInterval(() => {
      if (record && record.expiresAt <= Date.now()) setRecord(null);
    }, 60000);
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    return () => {
      clearInterval(expiry);
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, [scope, record]);

  return (
    <CookieConsentContext.Provider value={{ preferences, openPreferences, savePreferences }}>
      {children}
      <CookieBanner
        visible={!record}
        preferences={preferences}
        preferencesOpen={preferencesOpen}
        onOpen={openPreferences}
        onClose={() => setPreferencesOpen(false)}
        onSave={savePreferences}
      />
      {record?.persisted === false && <p className="cookie-save-status" role="status">Il browser non consente di memorizzare la scelta: le preferenze valgono per questa pagina.</p>}
    </CookieConsentContext.Provider>
  );
}

export function CookieConsentProvider({ scope = 'platform', children }) {
  return <ScopedCookieConsentProvider key={scope} scope={scope}>{children}</ScopedCookieConsentProvider>;
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
